import { Injectable } from '@angular/core';
import {
  ApplicationInsights,
  SeverityLevel,
} from '@microsoft/applicationinsights-web';
import { NGXLogger, NgxLoggerLevel } from 'ngx-logger';
import { AuthenticateResponse } from 'src/app/backend-client/models/Backend/Core/Api/Models/Identity/authenticate-response';
import { MxAppInitService } from './mx.app-init.service';

@Injectable({
  providedIn: 'root',
})
export class MxLoggingService {
  appInsights: ApplicationInsights;
  // https://geeklearning.io/monitor-your-angular-app-with-application-insights/

  accountForAppinsight: AuthenticateResponse;

  constructor(
    private logger: NGXLogger,
    private appInitService: MxAppInitService
  ) {}

  async load(): Promise<void> {
    if (this.appInitService.currentConfig) {
      if (this.appInitService.currentConfig.log.debug) {
        this.logger.updateConfig({
          level: NgxLoggerLevel.TRACE,
          disableConsoleLogging:
            !this.appInitService.currentConfig.log.consoleLogging,
          colorScheme: ['purple', 'teal', 'gray', 'gray', 'red', 'red', 'red'],
        });
      } else {
        this.logger.updateConfig({
          level: NgxLoggerLevel.WARN,
          disableConsoleLogging:
            !this.appInitService.currentConfig.log.consoleLogging,
          colorScheme: [
            'purple',
            'teal',
            'gray',
            'gray',
            'yellow',
            'red',
            'red',
          ],
        });
      }

      // Kapcsolati sztringgel a regionális ingestion végpontra megy a telemetria; a régi globális
      // dc.services.visualstudio.com végpont az ismeretlen/törölt kulcsot 400-zal dobja vissza.
      const ai = this.appInitService.currentConfig.appInsights;
      if (ai?.connectionString || ai?.instrumentationKey) {
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString: this.appInitService.currentConfig.appInsights.connectionString || undefined,
          instrumentationKey: this.appInitService.currentConfig.appInsights.connectionString
            ? undefined
            : this.appInitService.currentConfig.appInsights.instrumentationKey,
          enableAutoRouteTracking:
            this.appInitService.currentConfig.appInsights
              .enableAutoRouteTracking, // option to log all route changes
          autoTrackPageVisitTime:
            this.appInitService.currentConfig.appInsights
              .autoTrackPageVisitTime,
        },
      });
      this.appInsights.loadAppInsights();
      }
    } else {
      this.logger.updateConfig({
        level: NgxLoggerLevel.ERROR,
        disableConsoleLogging: false,
        colorScheme: ['purple', 'teal', 'gray', 'gray', 'yellow', 'red', 'red'],
      });
    }
  }

  trace(message: any, properties?: { [key: string]: any }) {
    if (properties) {
      this.logger.trace(message, properties);
    } else {
      this.logger.trace(message);
    }

    if (this.appInitService.currentConfig) {
      this.logAppInsightsTrace(message, properties);
    }
  }

  info(message: any, properties?: { [key: string]: any }) {
    if (properties) {
      this.logger.info(message, properties);
    } else {
      this.logger.info(message);
    }

    if (properties?.error) {
      this.logAppInsightsException(
        properties?.error,
        SeverityLevel.Information
      );
    }
  }

  log(message: any, properties?: { [key: string]: any }) {
    if (properties) {
      this.logger.log(message, properties);
    } else {
      this.logger.log(message);
    }
    if (this.appInitService.currentConfig) {
      if (this.shouldAppInsightsLog(NgxLoggerLevel.LOG)) {
        if (properties?.error) {
          this.logAppInsightsException(
            properties?.error,
            SeverityLevel.Verbose
          );
        }
      }
    }
  }

  warn(message: any, properties?: { [key: string]: any }) {
    if (properties) {
      this.logger.warn(message, properties);
    } else {
      this.logger.warn(message);
    }
    if (this.appInitService.currentConfig) {
      if (this.shouldAppInsightsLog(NgxLoggerLevel.WARN)) {
        if (properties?.error) {
          this.logAppInsightsException(
            properties?.error,
            SeverityLevel.Warning
          );
        }
      }
    }
  }

  error(message: any, properties?: { [key: string]: any }) {
    if (properties) {
      this.logger.error(message, properties);
    } else {
      this.logger.error(message);
    }
    if (this.appInitService.currentConfig) {
      if (this.shouldAppInsightsLog(NgxLoggerLevel.ERROR)) {
        if (properties?.error) {
          this.logAppInsightsException(properties?.error, SeverityLevel.Error);
        }
      }
    }
  }

  fatal(message: any, properties?: { [key: string]: any }) {
    if (properties) {
      this.logger.fatal(message, properties);
    } else {
      this.logger.fatal(message);
    }
    if (this.appInitService.currentConfig) {
      if (this.shouldAppInsightsLog(NgxLoggerLevel.FATAL)) {
        if (properties?.error) {
          this.logAppInsightsException(
            properties?.error,
            SeverityLevel.Critical
          );
        }
      }
    }
  }

  setAppInsightsAuthenticatedUserContext(account: AuthenticateResponse) {
    this.accountForAppinsight = account;
    this.appInsights?.setAuthenticatedUserContext(
      this.accountForAppinsight.id.toString()
    );
  }

  logAppInsightsPageView(
    name: string,
    url?: string,
    properties?: { [key: string]: string }
  ) {
    this.appInsights?.trackPageView({
      name: name,
      uri: url,
      properties: this.addAppInsightsGlobalProperties(properties),
      isLoggedIn: this.accountForAppinsight ? true : false,
    });
  }

  logAppInsightsEvent(name: string, properties?: { [key: string]: any }) {
    this.appInsights?.trackEvent({ name: name }, properties);
  }

  logAppInsightsMetric(
    name: string,
    average: number,
    properties?: { [key: string]: any }
  ) {
    this.appInsights?.trackMetric({ name: name, average: average }, properties);
  }

  logAppInsightsException(exception: Error, severityLevel?: number) {
    this.appInsights?.trackException({
      exception: exception,
      severityLevel: severityLevel,
    });
  }

  logAppInsightsTrace(message: string, properties?: { [key: string]: any }) {
    this.appInsights?.trackTrace({ message: message }, properties);
  }

  private addAppInsightsGlobalProperties(properties?: {
    [key: string]: string;
  }): { [key: string]: string } {
    if (!properties) {
      properties = {};
    }

    //add your custom properties such as app version

    return properties;
  }

  private shouldAppInsightsLog(level: NgxLoggerLevel) {
    if (this.logger.level === NgxLoggerLevel.OFF) {
      return false;
    } else if (this.logger.level === NgxLoggerLevel.INFO) {
      return level === NgxLoggerLevel.INFO;
    } else if (this.logger.level === NgxLoggerLevel.LOG) {
      return level === NgxLoggerLevel.INFO || level === NgxLoggerLevel.LOG;
    } else if (this.logger.level === NgxLoggerLevel.WARN) {
      return (
        level === NgxLoggerLevel.INFO ||
        level === NgxLoggerLevel.LOG ||
        level === NgxLoggerLevel.WARN
      );
    } else if (this.logger.level === NgxLoggerLevel.ERROR) {
      return (
        level === NgxLoggerLevel.INFO ||
        level === NgxLoggerLevel.LOG ||
        level === NgxLoggerLevel.WARN ||
        level === NgxLoggerLevel.ERROR
      );
    } else if (this.logger.level === NgxLoggerLevel.FATAL) {
      return (
        level === NgxLoggerLevel.INFO ||
        level === NgxLoggerLevel.LOG ||
        level === NgxLoggerLevel.WARN ||
        level === NgxLoggerLevel.ERROR ||
        level === NgxLoggerLevel.FATAL
      );
    } else {
      return true;
    }
  }
}
